import React, { useState, useEffect } from 'react';
import { Icons } from './Icons';
import { BookProject, BookGenre, WritingStyle } from '../types';
import * as GeminiService from '../services/geminiService';

export const ProjectWizard = ({ 
  onComplete, 
  onError,
  onCancel
}: { 
  onComplete: (project: BookProject) => void, 
  onError: (msg: string) => void,
  onCancel: () => void 
}) => {
  const [step, setStep] = useState(1);
  const [genre, setGenre] = useState<string>(BookGenre.LITERARY_FICTION);
  const [customGenre, setCustomGenre] = useState('');
  
  const [style, setStyle] = useState<string>(WritingStyle.CONTEMPORARY);
  const [customStyle, setCustomStyle] = useState('');

  const [language, setLanguage] = useState('English');
  const [customLanguage, setCustomLanguage] = useState('');

  const [wordCount, setWordCount] = useState<number>(50000);

  const [premise, setPremise] = useState('');
  const [worldSetting, setWorldSetting] = useState('');
  const [isRefining, setIsRefining] = useState(false);

  const [references, setReferences] = useState<{name: string, content: string}[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Computed values
  const finalGenre = customGenre || genre;
  const finalStyle = customStyle || style;
  const finalLanguage = customLanguage || language;

  // Fetch suggestions when genre changes
  useEffect(() => {
    let isActive = true;
    
    const fetchSuggestions = async () => {
      if (step === 2) {
        setLoadingSuggestions(true);
        // Clear old suggestions while loading new ones to avoid confusion
        setSuggestions([]); 
        
        try {
          const results = await GeminiService.suggestPremises(finalGenre, finalLanguage);
          if (isActive) {
            setSuggestions(results);
          }
        } catch (err) {
          console.warn("Could not fetch suggestions", err);
        } finally {
          if (isActive) {
            setLoadingSuggestions(false);
          }
        }
      }
    };

    fetchSuggestions();

    return () => {
      isActive = false;
    };
  }, [finalGenre, finalLanguage, step]);

  const handleRefreshSuggestions = async () => {
    setLoadingSuggestions(true);
    setSuggestions([]);
    try {
      const results = await GeminiService.suggestPremises(finalGenre, finalLanguage);
      setSuggestions(results);
    } catch (err) {
      console.warn("Could not fetch suggestions", err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Extract text from PDF using pdf.js
  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      // @ts-ignore - pdfjsLib is loaded via CDN in index.html
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      
      // Iterate through all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        // @ts-ignore
        const pageText = textContent.items.map((item: any) => item.str).join(' ');
        fullText += `--- Page ${i} ---\n${pageText}\n\n`;
      }
      return fullText;
    } catch (e) {
      console.error("PDF extraction error", e);
      throw new Error(`Could not parse PDF: ${file.name}`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files) as File[];
    
    setIsUploading(true);
    
    const fileReaders = files.map(file => {
      return new Promise<{name: string, content: string}>(async (resolve, reject) => {
        try {
          // Check for PDF
          if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
            const pdfText = await extractTextFromPDF(file);
            resolve({
              name: file.name,
              content: pdfText
            });
            return;
          }

          // Fallback for text files
          const reader = new FileReader();
          reader.onload = (event) => {
            if (event.target?.result) {
              resolve({
                name: file.name,
                content: event.target.result as string
              });
            } else {
              reject("Failed to read file");
            }
          };
          reader.readAsText(file);
        } catch (err) {
          reject(err);
        }
      });
    });

    Promise.all(fileReaders)
      .then(newRefs => {
        setReferences(prev => [...prev, ...newRefs]);
        setIsUploading(false);
      })
      .catch(err => {
        console.error(err);
        onError("Failed to read files. Please ensure they are valid text or PDF documents.");
        setIsUploading(false);
      });
  };

  const removeReference = (index: number) => {
    setReferences(refs => refs.filter((_, i) => i !== index));
  };

  const handleRefinePremise = async () => {
    if (!premise) return;
    setIsRefining(true);
    try {
      const refined = await GeminiService.refinePremise(premise, finalGenre, finalStyle, finalLanguage);
      setPremise(refined);
    } catch (err: any) {
      onError("Failed to refine premise: " + err.message);
    } finally {
      setIsRefining(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      // 1. Outline (passing references)
      const refContents = references.map(r => r.content);
      const outlineData = await GeminiService.generateBookOutline(
        null, 
        finalGenre, 
        finalStyle, 
        finalLanguage, 
        premise, 
        wordCount,
        refContents,
        worldSetting
      );
      
      // 2. Characters
      const characters = await GeminiService.generateCharacters(
        outlineData.synopsis, 
        finalStyle, 
        finalLanguage,
        worldSetting
      );

      // Create Project
      const newProject: BookProject = {
        id: crypto.randomUUID(),
        title: outlineData.title,
        synopsis: outlineData.synopsis,
        genre: finalGenre,
        style: finalStyle,
        styleGuide: outlineData.styleGuide, // Save the style guide!
        language: finalLanguage,
        targetWordCount: wordCount,
        tone: 'Serious',
        worldSetting: worldSetting,
        characters,
        worldBible: [], // Init empty
        outline: outlineData.chapters.map((c: any, i: number) => ({
          id: crypto.randomUUID(),
          number: i + 1,
          title: c.title,
          summary: c.summary,
          content: '',
          isDrafted: false
        })),
        museHistory: [], // Init empty chat
        createdAt: Date.now(),
        lastModified: Date.now()
      };

      onComplete(newProject);
    } catch (err: any) {
      onError(err.message || String(err));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full bg-white shadow-xl rounded-2xl overflow-hidden flex flex-col min-h-[600px] my-4">
        {/* Header */}
        <div className="bg-ink p-8 text-white relative">
          <div className="flex justify-between items-start relative z-10">
             <div>
                <h2 className="text-3xl font-serif">Create New Project</h2>
             </div>
             <button 
               type="button"
               onClick={(e) => {
                 e.stopPropagation();
                 onCancel();
               }}
               className="relative z-20 text-xs uppercase tracking-wider font-bold text-gray-400 hover:text-white hover:bg-white/10 px-3 py-2 rounded transition-colors cursor-pointer border border-transparent hover:border-white/20"
             >
               Cancel
             </button>
          </div>
          <div className="flex mt-4 space-x-2 relative z-10">
            {[1, 2, 3].map(i => (
              <div key={i} className={`h-1 flex-1 rounded-full ${step >= i ? 'bg-amber-400' : 'bg-gray-600'}`} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-8 flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="space-y-8 animate-fade-in-up">
              {/* Language Selection */}
              <div>
                <h3 className="text-lg font-bold mb-3">Language</h3>
                <div className="flex space-x-4">
                  <select 
                    className="flex-1 p-3 border rounded-lg bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-amber-500"
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      if (e.target.value !== 'Other') setCustomLanguage('');
                    }}
                  >
                    {['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Hindi', 'Arabic', 'Other'].map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  {language === 'Other' && (
                    <input 
                      type="text"
                      className="flex-1 p-3 border rounded-lg bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-amber-500"
                      placeholder="Type language..."
                      value={customLanguage}
                      onChange={(e) => setCustomLanguage(e.target.value)}
                    />
                  )}
                </div>
              </div>

              {/* Genre Selection */}
              <div>
                <h3 className="text-lg font-bold mb-3">Genre & Template</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.values(BookGenre).map(g => (
                    <button 
                      key={g} 
                      onClick={() => { setGenre(g); setCustomGenre(''); }}
                      className={`p-3 rounded-lg border text-left text-sm transition-all ${genre === g && !customGenre ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200 text-amber-900' : 'border-gray-200 hover:border-amber-300 text-gray-700'}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  <input 
                    type="text" 
                    placeholder="Or type a custom genre (e.g., 'Cyberpunk Noir', 'Cookbook')..."
                    className={`w-full p-3 border rounded-lg bg-white text-gray-900 transition-colors ${customGenre ? 'border-amber-500 ring-2 ring-amber-200' : 'border-gray-300'}`}
                    value={customGenre}
                    onChange={(e) => setCustomGenre(e.target.value)}
                  />
                </div>
              </div>

              {/* Style Selection */}
              <div>
                <h3 className="text-lg font-bold mb-3">Writing Style</h3>
                <select 
                  className={`w-full p-3 border rounded-lg bg-white text-gray-900 mb-3 ${style && !customStyle ? 'border-gray-300' : 'text-gray-500'}`}
                  value={style}
                  onChange={(e) => { setStyle(e.target.value); setCustomStyle(''); }}
                >
                  {Object.values(WritingStyle).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input 
                  type="text" 
                  placeholder="Or describe a specific author style (e.g., 'Like Kafka mixed with Dr. Seuss')..."
                  className={`w-full p-3 border rounded-lg bg-white text-gray-900 ${customStyle ? 'border-amber-500 ring-2 ring-amber-200' : 'border-gray-300'}`}
                  value={customStyle}
                  onChange={(e) => setCustomStyle(e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-fade-in-up">
              
              {/* Word Count Estimation */}
              <div className="bg-amber-50 p-6 rounded-lg border border-amber-100">
                <h3 className="text-lg font-bold mb-2 text-amber-900">Estimated Book Length</h3>
                <p className="text-sm text-gray-600 mb-4">BookWiz will adjust the pacing and number of chapters based on your target length.</p>
                <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-4 sm:space-y-0">
                   <div className="flex-1 w-full">
                      <input 
                        type="range" 
                        min="5000" 
                        max="150000" 
                        step="1000"
                        value={wordCount}
                        onChange={(e) => setWordCount(parseInt(e.target.value))}
                        className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>Short (5k)</span>
                        <span>Novella (40k)</span>
                        <span>Novel (80k+)</span>
                        <span>Epic</span>
                      </div>
                   </div>
                   <div className="w-full sm:w-40">
                     <div className="relative">
                        <input 
                          type="number" 
                          value={wordCount}
                          onChange={(e) => setWordCount(parseInt(e.target.value))}
                          className="w-full p-2 pr-14 border border-amber-300 rounded font-bold text-amber-900 text-right bg-white"
                        />
                        <span className="absolute right-3 top-2 text-xs text-amber-600 font-medium pointer-events-none">words</span>
                     </div>
                   </div>
                </div>
              </div>

              {/* Reference Material Section */}
              <div className="bg-gray-50 p-6 rounded-lg border border-dashed border-gray-300">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Reference Materials (Optional)</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Upload files (PDF, txt, md, json) to inspire the AI. <br/>
                      The AI will create a <strong>unique</strong> book with a similar vibe, but will not copy content.
                    </p>
                  </div>
                  <label className="cursor-pointer bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg flex items-center shadow-sm transition-colors">
                    <span className="mr-2"><Icons.Upload /></span>
                    Add Files
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".txt,.md,.json,.csv,.pdf"
                      multiple
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>
                
                {/* Warning Banner for Token usage */}
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4 rounded-r">
                   <div className="flex items-start">
                     <div className="flex-shrink-0 text-amber-600 mt-0.5">
                       <Icons.Info />
                     </div>
                     <div className="ml-3">
                       <p className="text-xs md:text-sm text-amber-800">
                         <span className="font-bold">Note for large files:</span> If you are uploading entire books or scripts, please use a <strong className="font-bold">Paid API Key</strong>. 
                         Free tier accounts have strict token limits and may encounter "Quota Exceeded" errors with large reference files, forcing you to wait for the quota to reset.
                       </p>
                     </div>
                   </div>
                </div>

                {isUploading && <p className="text-xs text-amber-600 animate-pulse mb-2">Reading files...</p>}

                {references.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {references.map((ref, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 text-sm">
                        <span className="truncate font-medium text-gray-700 flex-1">{ref.name}</span>
                        <span className="text-xs text-gray-400 mx-2">{(ref.content.length / 1000).toFixed(1)}k chars</span>
                        <button 
                          onClick={() => removeReference(idx)}
                          className="text-red-400 hover:text-red-600 p-1"
                        >
                          <Icons.Trash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* World Setting (New) */}
              <div>
                <h3 className="text-xl font-bold mb-2">World Setting (Optional)</h3>
                <p className="text-sm text-gray-600 mb-2">Describe the world, rules of magic, technology level, or time period.</p>
                <textarea 
                  className="w-full h-24 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-gray-900"
                  placeholder="e.g. A dystopian city where rain never stops, or a medieval kingdom ruled by cats..."
                  value={worldSetting}
                  onChange={(e) => setWorldSetting(e.target.value)}
                />
              </div>

              {/* Premise Section (Enhanced) */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-xl font-bold">The Premise</h3>
                  <button 
                    onClick={handleRefinePremise}
                    disabled={!premise || isRefining}
                    className="text-xs flex items-center bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full hover:bg-indigo-100 transition-colors border border-indigo-200 disabled:opacity-50"
                  >
                    {isRefining ? <span className="animate-spin mr-1">⚡</span> : <span className="mr-1"><Icons.Sparkles /></span>}
                    {isRefining ? 'Refining...' : 'Refine with AI'}
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-2">What is your book about? Be as detailed or vague as you like.</p>
                <textarea 
                  className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none bg-white text-gray-900 text-lg"
                  placeholder={references.length > 0 
                    ? "Since you added reference materials, tell the AI how to use them. E.g., 'Use the dark humor of File 1 but the pacing of File 2 to tell a story about...'"
                    : "A lonely astronaut discovers a flower growing on Mars, only to realize it's communicating with him..."
                  }
                  value={premise}
                  onChange={(e) => setPremise(e.target.value)}
                />
              </div>
              
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                   <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">AI Suggestions for {finalGenre}</p>
                   <button 
                     onClick={handleRefreshSuggestions} 
                     className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                     disabled={loadingSuggestions}
                   >
                     {loadingSuggestions ? <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div> : <Icons.Sparkles className="w-3 h-3" />}
                     Refresh
                   </button>
                </div>
                
                <div className="space-y-2">
                  {loadingSuggestions && suggestions.length === 0 ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-10 bg-gray-100 rounded"></div>
                      <div className="h-10 bg-gray-100 rounded"></div>
                    </div>
                  ) : (
                    suggestions.map((s, i) => (
                      <button 
                        key={i}
                        onClick={() => setPremise(s)}
                        className="block w-full text-left p-3 text-sm bg-gray-50 hover:bg-amber-50 rounded-lg border border-transparent hover:border-amber-200 transition-colors text-gray-700"
                      >
                        {s}
                      </button>
                    ))
                  )}
                  {!loadingSuggestions && suggestions.length === 0 && (
                     <div className="text-sm text-gray-400 italic">No suggestions available.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-12 animate-fade-in-up">
              <div className="mb-6 inline-block p-4 bg-amber-100 rounded-full text-amber-600">
                <Icons.Book />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-2">Ready to Outline</h3>
              <div className="max-w-md mx-auto mb-8 text-gray-600">
                <p className="mb-2">We are about to generate a structure for:</p>
                <div className="bg-gray-50 p-4 rounded-lg text-sm text-left border border-gray-200 space-y-1">
                   <p><strong>Genre:</strong> {finalGenre}</p>
                   <p><strong>Target Length:</strong> {wordCount.toLocaleString()} words</p>
                   <p><strong>Language:</strong> {finalLanguage}</p>
                   <p><strong>Style:</strong> {finalStyle}</p>
                   {references.length > 0 && <p><strong>References:</strong> {references.length} file(s) uploaded</p>}
                   {worldSetting && <p><strong>World:</strong> Custom setting defined</p>}
                </div>
              </div>
              
              {isGenerating ? (
                 <div className="flex flex-col items-center space-y-4">
                   <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                   <p className="text-amber-600 font-medium animate-pulse">Crafting your story architecture...</p>
                   {references.length > 0 && <p className="text-xs text-gray-400">Analyzing reference materials...</p>}
                 </div>
              ) : (
                <button 
                  onClick={handleGenerate}
                  className="px-8 py-3 bg-ink text-white rounded-lg font-bold hover:bg-gray-800 shadow-lg transform transition hover:-translate-y-1"
                >
                  Generate Book Structure
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
          <div>
            {step > 1 && !isGenerating ? (
              <button onClick={() => setStep(s => s - 1)} className="px-6 py-2 text-gray-600 font-medium">Back</button>
            ) : <div></div>}
          </div>
          
          <div>
             {step < 3 && (
              <button 
                onClick={() => setStep(s => s + 1)} 
                className="px-6 py-2 bg-ink text-white rounded-lg font-medium"
                disabled={step === 2 && !premise}
              >
                Next Step
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};